import { ConfirmModal } from '@/components/ConfirmModal';
import { StatusModal } from '@/components/StatusModal';
import { LatkaPicker } from '@/components/sklad/LatkaPicker';
import {
  type MaterialFormRow,
  MaterialOrderRows,
  newMaterialRow,
} from '@/components/sklad/MaterialOrderRows';
import {
  type Material,
  type MaterialOrderItem,
  formatAmount,
} from '@/components/sklad/materialTypes';
import { type Latka, formatDate, formatQty, parseQty } from '@/components/sklad/types';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

type Item = {
  id: number;
  serial_number: number;
  latka_id: string;
  latka_code: string;
  latka_name: string;
  latka_type: string;
  latka_image: string;
  unit: string;
  quantity: number;
  received: number;
};

type Order = {
  id: number;
  supplier: string;
  note: string;
  expected_at: number | null;
  status: string;
  created_by: string;
  created_at: number;
  items: Item[];
  /** Drobný materiál (zipy, knoflíky…) — ve stejné objednávce jako role látek. */
  materials: MaterialOrderItem[];
};

/** Řádek formuláře = jedna objednaná role. Stejná látka smí být víckrát. */
type FormItem = { key: string; latka: Latka | null; quantity: string };

const CARD = 'bg-surface rounded-xl shadow-card';
const INPUT =
  'w-full text-sm text-text bg-surface border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all';

const STATUS: Record<string, { label: string; cls: string }> = {
  ordered: { label: 'Objednáno', cls: 'text-primary bg-primary-50' },
  partial: { label: 'Částečně přijato', cls: 'text-warning bg-warning-light' },
  received: { label: 'Přijato', cls: 'text-success bg-success-light' },
};

function newFormItem(): FormItem {
  return { key: crypto.randomUUID(), latka: null, quantity: '' };
}

/** Proužek kolečkových náhledů látek v objednávce — stejná látka jen jednou. */
function FabricThumbs({ items }: { items: Item[] }) {
  const seen = new Set<string>();
  const unique = items.filter((it) => {
    const key = it.latka_image || it.latka_code || it.latka_name;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  if (unique.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {unique.map((it) => (
        <div
          key={it.id}
          title={`${it.latka_name}${it.latka_type ? ` · ${it.latka_type}` : ''}`}
          className="w-7 h-7 rounded-full overflow-hidden border border-border bg-bg shrink-0"
        >
          {it.latka_image && (
            <img
              src={it.latka_image}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover"
            />
          )}
        </div>
      ))}
    </div>
  );
}

function PencilIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function toDateInput(ms: number | null): string {
  if (!ms) return '';
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Objednávky materiálu — hlavička + položky, kde každá položka je jedna role. */
export function SkladObjednavkyPage() {
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [tab, setTab] = useState<'open' | 'received'>('open');
  const [latky, setLatky] = useState<Latka[]>([]);
  const [latkyLoading, setLatkyLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Order | null>(null);
  const [supplier, setSupplier] = useState('');
  const [note, setNote] = useState('');
  const [noteOpen, setNoteOpen] = useState(false);
  const [expectedAt, setExpectedAt] = useState('');
  const [formItems, setFormItems] = useState<FormItem[]>([newFormItem()]);
  const [materialRows, setMaterialRows] = useState<MaterialFormRow[]>([newMaterialRow()]);
  const [catalog, setCatalog] = useState<Material[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<Order | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch<{ orders: Order[] }>('/api/sklad/orders');
      setOrders(d.orders);
    } catch (e: unknown) {
      setOrders([]);
      setToast({ msg: e instanceof ApiError ? e.message : 'Chyba načítání', kind: 'error' });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Látky načteme až při prvním otevření formuláře — na výpisu je nepotřebujeme.
  const loadLatky = useCallback(async () => {
    if (latky.length > 0 || latkyLoading) return;
    setLatkyLoading(true);
    try {
      const d = await apiFetch<{ latky: Latka[] }>('/api/sklad/latky');
      setLatky(d.latky);
    } catch {
      setToast({ msg: 'Nepodařilo se načíst látky', kind: 'error' });
    } finally {
      setLatkyLoading(false);
    }
  }, [latky.length, latkyLoading]);

  // Katalog drobného materiálu — čerstvě při každém otevření formuláře (stav skladu).
  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const d = await apiFetch<{ items: Material[] }>('/api/material/items');
      setCatalog(d.items);
    } catch {
      setToast({ msg: 'Nepodařilo se načíst drobný materiál', kind: 'error' });
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  const suppliers = useMemo(
    () =>
      [...new Set((orders ?? []).map((o) => o.supplier).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, 'cs'),
      ),
    [orders],
  );

  // Dva taby: „Objednáno" (i částečně přijaté — pořád je na co čekat) a
  // „Přijato" (kompletně vyřízené, historie).
  const openOrders = useMemo(
    () =>
      (orders ?? [])
        .filter((o) => o.status !== 'received')
        .sort((a, b) => {
          const ea = a.expected_at ?? Number.POSITIVE_INFINITY;
          const eb = b.expected_at ?? Number.POSITIVE_INFINITY;
          return ea !== eb ? ea - eb : b.created_at - a.created_at;
        }),
    [orders],
  );
  const receivedOrders = useMemo(
    () =>
      (orders ?? [])
        .filter((o) => o.status === 'received')
        .sort((a, b) => b.created_at - a.created_at),
    [orders],
  );
  const sortedOrders = tab === 'open' ? openOrders : receivedOrders;

  function openCreate() {
    setEditing(null);
    setSupplier('');
    setNote('');
    setNoteOpen(false);
    setExpectedAt('');
    setFormItems([newFormItem()]);
    setMaterialRows([newMaterialRow()]);
    setModalOpen(true);
    loadLatky();
    loadCatalog();
  }

  function openEdit(order: Order) {
    setEditing(order);
    setSupplier(order.supplier);
    setNote(order.note);
    setNoteOpen(Boolean(order.note));
    setExpectedAt(toDateInput(order.expected_at));
    // Předvyplní jen nepřijaté role — přijaté se editací nesmí rozbít.
    setFormItems([
      ...order.items
        .filter((i) => !i.received)
        .map((i) => ({
          key: crypto.randomUUID(),
          latka: {
            id: i.latka_id,
            code: i.latka_code,
            name: i.latka_name,
            type: i.latka_type,
            image: i.latka_image,
            pricePerMeter: 0,
          },
          quantity: String(i.quantity),
        })),
      newFormItem(),
    ]);
    setMaterialRows([
      ...order.materials
        .filter((m) => !m.received)
        .map((m) => ({
          key: crypto.randomUUID(),
          materialId: m.material_id,
          quantity: String(m.quantity),
        })),
      newMaterialRow(),
    ]);
    setModalOpen(true);
    loadLatky();
    loadCatalog();
  }

  // Výběr látky v posledním (prázdném) řádku rovnou přidá další prázdný.
  function selectLatka(key: string, latka: Latka) {
    setFormItems((prev) => {
      const idx = prev.findIndex((it) => it.key === key);
      const wasEmpty = idx >= 0 && !prev[idx]?.latka;
      const next = prev.map((it) => (it.key === key ? { ...it, latka } : it));
      if (idx === prev.length - 1 && wasEmpty) next.push(newFormItem());
      return next;
    });
  }

  async function save() {
    const items = formItems
      .filter(
        (it) => it.latka && Number.isFinite(parseQty(it.quantity)) && parseQty(it.quantity) > 0,
      )
      .map((it) => ({
        latka_id: it.latka?.id ?? '',
        latka_code: it.latka?.code ?? '',
        latka_name: it.latka?.name ?? '',
        latka_type: it.latka?.type ?? '',
        latka_image: it.latka?.image ?? '',
        quantity: parseQty(it.quantity),
      }));
    const materials = materialRows
      .map((r) => ({ material_id: r.materialId, quantity: parseQty(r.quantity) }))
      .filter(
        (r): r is { material_id: number; quantity: number } =>
          r.material_id !== null && Number.isFinite(r.quantity) && r.quantity > 0,
      );
    if (!editing && items.length === 0 && materials.length === 0) {
      setToast({ msg: 'Přidej alespoň jednu látku nebo materiál s množstvím', kind: 'error' });
      return;
    }
    setSaving(true);
    try {
      const body = {
        supplier,
        // Odškrtnutá poznámka = žádná (jinak by tam zůstal starý text).
        note: noteOpen ? note : '',
        expected_at: expectedAt ? new Date(`${expectedAt}T00:00:00`).getTime() : null,
        items,
        materials,
      };
      if (editing) {
        await apiFetch(`/api/sklad/orders/${editing.id}`, { method: 'PATCH', body });
      } else {
        await apiFetch('/api/sklad/orders', { method: 'POST', body });
      }
      setModalOpen(false);
      await load();
      setToast({ msg: editing ? 'Objednávka upravena' : 'Objednávka založena', kind: 'success' });
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.describe() : 'Uložení selhalo', kind: 'error' });
    } finally {
      setSaving(false);
    }
  }

  const receivedInEdit = [
    ...(editing?.items.filter((i) => i.received) ?? []),
    ...(editing?.materials.filter((m) => m.received) ?? []),
  ];

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-text">Objednávky materiálu</h1>
          <p className="text-sm text-text-secondary mt-1">Co objednáváš a kdy to má dorazit</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 cursor-pointer transition-colors shrink-0"
        >
          + Nová objednávka
        </button>
      </div>

      <div className="flex items-center gap-1 mb-5 border-b border-border">
        {(
          [
            ['open', 'Objednáno', openOrders.length],
            ['received', 'Přijato', receivedOrders.length],
          ] as const
        ).map(([key, label, count]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`-mb-px px-4 py-2.5 text-sm font-semibold cursor-pointer border-b-2 transition-colors ${
              tab === key
                ? 'border-primary text-primary'
                : 'border-transparent text-text-secondary hover:text-text'
            }`}
          >
            {label}
            <span className="ml-1.5 text-[11px] font-semibold px-1.5 py-0.5 rounded bg-bg text-text-muted">
              {count}
            </span>
          </button>
        ))}
      </div>

      <div className={`${CARD} overflow-x-auto`}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-bg text-left text-xs font-semibold text-text-muted uppercase tracking-wider">
              <th className="w-8 px-3 py-3" />
              <th className="px-5 py-3">Dodavatel</th>
              <th className="px-5 py-3">Položek</th>
              <th className="px-5 py-3">Stav</th>
              <th className="px-5 py-3">Očekáváno</th>
              <th className="px-5 py-3">Vytvořil</th>
              <th className="px-5 py-3 text-right">Akce</th>
            </tr>
          </thead>
          <tbody>
            {orders === null && (
              <tr>
                <td colSpan={7} className="px-5 py-8 text-center text-text-muted">
                  Načítám…
                </td>
              </tr>
            )}
            {orders !== null && sortedOrders.length === 0 && (
              <tr>
                <td colSpan={7} className="px-5 py-8 text-center text-text-muted">
                  {tab === 'open' ? 'Žádná otevřená objednávka. 🎉' : 'Zatím nic přijatého.'}
                </td>
              </tr>
            )}
            {sortedOrders.map((o) => {
              const st = STATUS[o.status] ?? STATUS.ordered;
              const isOpen = expanded.has(o.id);
              const receivedCount =
                o.items.filter((i) => i.received).length +
                o.materials.filter((m) => m.received).length;
              return (
                <Fragment key={o.id}>
                  <tr className="border-b border-border-light hover:bg-surface-hover transition-colors">
                    <td className="px-3 py-3 text-center">
                      <button
                        type="button"
                        onClick={() =>
                          setExpanded((prev) => {
                            const next = new Set(prev);
                            if (next.has(o.id)) next.delete(o.id);
                            else next.add(o.id);
                            return next;
                          })
                        }
                        title="Zobrazit položky"
                        className={`text-text-muted hover:text-text cursor-pointer transition-transform inline-block ${
                          isOpen ? 'rotate-90' : ''
                        }`}
                      >
                        ›
                      </button>
                    </td>
                    <td className="px-5 py-3">
                      <div className="text-text font-medium">{o.supplier || '—'}</div>
                      <FabricThumbs items={o.items} />
                    </td>
                    <td className="px-5 py-3 text-text-secondary">
                      {receivedCount}/{o.items.length + o.materials.length}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`text-[11px] font-semibold px-2 py-0.5 rounded ${st?.cls ?? ''}`}
                      >
                        {st?.label}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-text-secondary whitespace-nowrap">
                      {formatDate(o.expected_at)}
                    </td>
                    <td className="px-5 py-3 text-text-secondary">{o.created_by}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => openEdit(o)}
                          title="Upravit"
                          className="text-text-secondary hover:text-primary cursor-pointer transition-colors"
                        >
                          <PencilIcon />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(o)}
                          title="Smazat"
                          className="text-text-secondary hover:text-danger cursor-pointer transition-colors"
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-bg border-b border-border-light">
                      <td />
                      <td colSpan={6} className="px-5 py-3">
                        {o.note && (
                          <p className="text-xs text-text-secondary mb-2">Poznámka: {o.note}</p>
                        )}
                        <div className="space-y-1">
                          {o.items.map((i) => (
                            <div key={i.id} className="flex items-center gap-3 text-sm">
                              <span
                                className={`w-2 h-2 rounded-full shrink-0 ${
                                  i.received ? 'bg-success' : 'bg-text-muted'
                                }`}
                                title={i.received ? 'Přijato' : 'Čeká na příjem'}
                              />
                              <span className="font-semibold text-text w-12">
                                č. {i.serial_number}
                              </span>
                              <span className="text-text-secondary flex-1">
                                {i.latka_name}
                                {i.latka_type ? ` · ${i.latka_type}` : ''}
                              </span>
                              <span className="text-text font-medium">
                                {formatQty(i.quantity, i.unit)}
                              </span>
                            </div>
                          ))}
                          {o.materials.map((m) => (
                            <div key={`m${m.id}`} className="flex items-center gap-3 text-sm">
                              <span
                                className={`w-2 h-2 rounded-full shrink-0 ${
                                  m.received ? 'bg-success' : 'bg-text-muted'
                                }`}
                                title={m.received ? 'Přijato' : 'Čeká na příjem'}
                              />
                              <span className="font-semibold text-text-muted w-12 text-xs">
                                {m.category || 'mat.'}
                              </span>
                              <span className="text-text-secondary flex-1">{m.label}</span>
                              <span className="text-text font-medium">
                                {formatAmount(m.quantity, m.unit)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <OrderModal
          editing={!!editing}
          supplier={supplier}
          suppliers={suppliers}
          note={note}
          expectedAt={expectedAt}
          items={formItems}
          latky={latky}
          latkyLoading={latkyLoading}
          receivedCount={receivedInEdit.length}
          saving={saving}
          onSupplier={setSupplier}
          onNote={setNote}
          noteOpen={noteOpen}
          onNoteOpen={setNoteOpen}
          onExpectedAt={setExpectedAt}
          onSelectLatka={selectLatka}
          onQuantity={(key, v) =>
            setFormItems((prev) => prev.map((it) => (it.key === key ? { ...it, quantity: v } : it)))
          }
          onRemove={(key) =>
            setFormItems((prev) => {
              const next = prev.filter((it) => it.key !== key);
              return next.length > 0 ? next : [newFormItem()];
            })
          }
          onAddRow={() => setFormItems((prev) => [...prev, newFormItem()])}
          materialSection={
            <MaterialOrderRows
              rows={materialRows}
              catalog={catalog}
              loading={catalogLoading}
              onChange={setMaterialRows}
            />
          }
          onSave={save}
          onClose={() => setModalOpen(false)}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Smazat objednávku"
          message={
            <>
              Opravdu smazat objednávku od{' '}
              <strong>{deleteTarget.supplier || 'bez dodavatele'}</strong>? Smažou se i všechny její
              role včetně přijatých. Už přijatý drobný materiál zůstane na skladě.
            </>
          }
          confirmText="Smazat"
          confirmKind="danger"
          onCancel={() => setDeleteTarget(null)}
          onConfirm={async () => {
            const id = deleteTarget.id;
            setDeleteTarget(null);
            try {
              await apiFetch(`/api/sklad/orders/${id}`, { method: 'DELETE' });
              await load();
              setToast({ msg: 'Objednávka smazána', kind: 'success' });
            } catch (e: unknown) {
              setToast({
                msg: e instanceof ApiError ? e.describe() : 'Mazání selhalo',
                kind: 'error',
              });
            }
          }}
        />
      )}

      {toast && (
        <StatusModal message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />
      )}
    </div>
  );
}

/** Formulář objednávky — hlavička + řádky rolí. */
function OrderModal({
  editing,
  supplier,
  suppliers,
  note,
  expectedAt,
  items,
  latky,
  latkyLoading,
  receivedCount,
  saving,
  onSupplier,
  onNote,
  noteOpen,
  onNoteOpen,
  onExpectedAt,
  onSelectLatka,
  onQuantity,
  onRemove,
  onAddRow,
  materialSection,
  onSave,
  onClose,
}: {
  editing: boolean;
  supplier: string;
  suppliers: string[];
  note: string;
  expectedAt: string;
  items: FormItem[];
  latky: Latka[];
  latkyLoading: boolean;
  receivedCount: number;
  saving: boolean;
  onSupplier: (v: string) => void;
  onNote: (v: string) => void;
  noteOpen: boolean;
  onNoteOpen: (v: boolean) => void;
  onExpectedAt: (v: string) => void;
  onSelectLatka: (key: string, l: Latka) => void;
  onQuantity: (key: string, v: string) => void;
  onRemove: (key: string) => void;
  onAddRow: () => void;
  materialSection: React.ReactNode;
  onSave: () => void;
  onClose: () => void;
}) {
  useEscapeKey(onClose);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onClose}
        className="fixed inset-0 bg-black/30 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-2xl my-10 bg-surface rounded-2xl shadow-modal p-6 reveal">
        <h2 className="text-lg font-bold text-text pb-4 mb-4 border-b border-border">
          {editing ? 'Upravit objednávku' : 'Nová objednávka'}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-sm font-medium text-text-secondary block mb-1.5">Dodavatel</span>
            <input
              list="sklad-suppliers"
              value={supplier}
              onChange={(e) => onSupplier(e.target.value)}
              autoComplete="off"
              className={INPUT}
              placeholder="Název dodavatele"
            />
            <datalist id="sklad-suppliers">
              {suppliers.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-text-secondary block mb-1.5">
              Očekávané dodání
            </span>
            <input
              type="date"
              value={expectedAt}
              onChange={(e) => onExpectedAt(e.target.value)}
              className={INPUT}
            />
          </label>
        </div>

        {editing && receivedCount > 0 && (
          <p className="text-xs text-text-muted mt-4">
            {receivedCount} {receivedCount === 1 ? 'položka je' : 'položek je'} už přijatých — ty se
            editací nemění a v seznamu níž nejsou.
          </p>
        )}

        <div className="mt-4">
          <span className="text-sm font-medium text-text-secondary block mb-2">
            {editing ? 'Látky k objednání' : 'Látky'}
          </span>
          <div className="space-y-2">
            {items.map((it, idx) => (
              <div
                key={it.key}
                className="flex items-start gap-2 bg-bg rounded-lg border border-border p-2.5"
              >
                <span className="text-sm font-semibold text-text-muted w-5 text-right pt-2 shrink-0">
                  {idx + 1}.
                </span>
                <div className="flex-1 min-w-0">
                  <LatkaPicker
                    value={it.latka}
                    options={latky}
                    loading={latkyLoading}
                    onChange={(l) => onSelectLatka(it.key, l)}
                  />
                </div>
                <div className="flex items-center gap-1 w-32 shrink-0 pt-0.5">
                  <input
                    value={it.quantity}
                    onChange={(e) => onQuantity(it.key, e.target.value)}
                    inputMode="decimal"
                    placeholder="Množství"
                    className={`${INPUT} text-right`}
                  />
                  <span className="text-xs text-text-muted">m</span>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(it.key)}
                  className="text-text-muted hover:text-danger px-1 pt-2 cursor-pointer shrink-0"
                  title="Odebrat položku"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={onAddRow}
            className="text-sm font-semibold text-primary hover:text-primary-dark mt-2 cursor-pointer"
          >
            + Přidat látku
          </button>
        </div>

        {materialSection}

        <div className="mt-4">
          <label className="flex items-center gap-2 cursor-pointer select-none w-fit">
            <input
              type="checkbox"
              checked={noteOpen}
              onChange={(e) => onNoteOpen(e.target.checked)}
              className="w-4 h-4 rounded border-border cursor-pointer accent-primary"
            />
            <span className="text-sm font-medium text-text-secondary">Přidat poznámku</span>
          </label>
          {noteOpen && (
            <input
              value={note}
              onChange={(e) => onNote(e.target.value)}
              className={`${INPUT} mt-2`}
              placeholder="Např. číslo objednávky u dodavatele"
            />
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover transition-colors cursor-pointer"
          >
            Zrušit
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 cursor-pointer transition-colors disabled:opacity-50"
          >
            {saving ? 'Ukládám…' : editing ? 'Uložit' : 'Vytvořit objednávku'}
          </button>
        </div>
      </div>
    </div>
  );
}
